import React, { useCallback, useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Stage, Container, Graphics } from "@pixi/react";
import { useApp } from "../context/AppContext";
import Rectangle from "./shapes/Rectangle";
import Circle from "./shapes/Circle";
import Triangle from "./shapes/Triangle";
import Door from "./shapes/Door";
import EditableLine from "./shapes/EditableLine";
import Icon from "./shapes/Icon";
import Arrow from "./shapes/Arrow";
import FreehandDraw from "./shapes/FreehandDraw";
import MergedStructure from "./shapes/MergedStructure";
import MergeSystem from "./MergeSystem";
import ClosedShape from "./ClosedShape";
import ZoomBar from "./ZoomBar";
import "@pixi/events";
import AnnotationText from './annotations/AnnotationText';
import PolygonRoom from "./shapes/PolygonRoom";

// Grid component for rendering grid pattern
const Grid = ({ width, height, gridSize = 50, color = 0x000000, alpha = 0.1 }) => {
  const draw = useCallback((graphics) => {
    graphics.clear();
    graphics.lineStyle(1, color, alpha);
    
    // Draw vertical lines
    for (let x = 0; x <= width; x += gridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, height);
    }
    
    // Draw horizontal lines
    for (let y = 0; y <= height; y += gridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(width, y);
    }
  }, [width, height, gridSize, color, alpha]);

  return <Graphics draw={draw} />;
};

const Canvas = forwardRef((props, ref) => {
  // Get only the needed props from useApp
  const {
    shapes,
    selectedShapeId,
    currentTool,
    updateShape,
    selectShape,
    addShape,
    registerStage,
    getCanvasCoordinates,
    setTool,
    closedShapes
  } = useApp();

  // Get annotations and drawing props from props (from FloorPlanner)
  const { 
    annotations = [], 
    setAnnotations,
    isDrawingMode = false,
    drawingColor = { hex: 0xFFEB3B },
    drawingLineWidth = 8,
    selectedMeasurementUnit,
    showGrid = false,
    isBlueprintMode = false
  } = props;
  console.log('Canvas annotations:', annotations);

  // Calculate grid color based on blueprint mode
  const gridColor = isBlueprintMode ? 0xffffff : 0x000000; // White for blueprint, black for normal
  const gridAlpha = isBlueprintMode ? 0.2 : 0.1; // Slightly more visible in blueprint mode

  const stageRef = useRef(null);
  const propagationBlocked = useRef(false);
  const [isStageRegistered, setIsStageRegistered] = useState(false);
  const [mergedStructures, setMergedStructures] = useState([]);
  const [nextStructureId, setNextStructureId] = useState(1);
  
  // Preview state for shape creation
  const [isCreatingShape, setIsCreatingShape] = useState(false);
  const [previewShape, setPreviewShape] = useState(null);
  const [creationStartPoint, setCreationStartPoint] = useState(null);
  
  // Drawing state for freehand drawing
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawingPath, setCurrentDrawingPath] = useState(null);
  const [drawingPaths, setDrawingPaths] = useState([]);
  const [nextDrawingId, setNextDrawingId] = useState(1);
  
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: window.innerWidth - 400,
    height: window.innerHeight - 100
  });

  // Wall alignment and angle detection state
  const [wallAlignmentMode, setWallAlignmentMode] = useState(true); // Enable by default
  const [snapThreshold, setSnapThreshold] = useState(15); // pixels
  const [angleThreshold, setAngleThreshold] = useState(10); // degrees

  // Zoom and pan state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanMode, setIsPanMode] = useState(false);
  const [isZoomMode, setIsZoomMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStartPoint, setPanStartPoint] = useState(null);
  
  // Zoom constants
  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.1;

  // Zoom and pan functions
  const handleResetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handleTogglePan = useCallback(() => {
    setIsPanMode(prev => !prev);
    if (isZoomMode) setIsZoomMode(false); // Turn off zoom mode when pan is activated
  }, [isZoomMode]);

  const handleToggleZoom = useCallback(() => {
    setIsZoomMode(prev => !prev);
    if (isPanMode) setIsPanMode(false); // Turn off pan mode when zoom is activated
  }, [isPanMode]);

  // Update coordinate transformation to account for zoom and pan
  const transformCoordinates = useCallback((clientX, clientY) => {
    const stage = stageRef.current;
    if (!stage) return null;

    const canvas = stage._canvas;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
    const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

    // Convert screen coordinates to canvas coordinates
    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;

    // Apply inverse transformations in the correct order
    const worldX = (canvasX - panOffset.x) / zoomLevel;
    const worldY = (canvasY - panOffset.y) / zoomLevel;

    return { x: worldX, y: worldY };
  }, [zoomLevel, panOffset]);

  // Mouse wheel zoom support
  useEffect(() => {
    const handleWheel = (event) => {
      // Only zoom if zoom mode is active
      if (isZoomMode) {
        event.preventDefault();
        
        // Get mouse position relative to canvas
        const canvas = stageRef.current?._canvas;
        if (!canvas) return;
        
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        
        // Convert to canvas coordinates
        const scaleX = stageRef.current.app ? stageRef.current.app.screen.width / rect.width : 1;
        const scaleY = stageRef.current.app ? stageRef.current.app.screen.height / rect.height : 1;
        
        const canvasMouseX = mouseX * scaleX;
        const canvasMouseY = mouseY * scaleY;
        
        // Calculate zoom
        const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomLevel + delta));
        const zoomFactor = newZoom / zoomLevel;
        
        // Calculate new pan to zoom at cursor position
        const newPanX = canvasMouseX - (canvasMouseX - panOffset.x) * zoomFactor;
        const newPanY = canvasMouseY - (canvasMouseY - panOffset.y) * zoomFactor;
        
        setZoomLevel(newZoom);
        setPanOffset({ x: newPanX, y: newPanY });
      }
    };

    const canvasElement = stageRef.current?._canvas;
    if (canvasElement) {
      canvasElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => canvasElement.removeEventListener('wheel', handleWheel);
    }
  }, [isZoomMode, zoomLevel, panOffset]);

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Zoom tool shortcut (Z key)
      if (event.key === 'z' || event.key === 'Z') {
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          handleToggleZoom();
        }
      }
      
      // Space bar for pan mode
      if (event.key === ' ' && !event.repeat) {
        event.preventDefault();
        setIsPanMode(true);
      }
      
      // Reset zoom with Ctrl/Cmd + 0
      if ((event.ctrlKey || event.metaKey) && event.key === '0') {
        event.preventDefault();
        handleResetZoom();
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === ' ') {
        setIsPanMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleToggleZoom, handleResetZoom]);

  // Update canvas dimensions on window resize
  useEffect(() => {
    const updateDimensions = () => {
      const newDimensions = {
        width: window.innerWidth - 400,
        height: window.innerHeight - 100
      };
      setCanvasDimensions(newDimensions);
      
      // Update PIXI stage dimensions if available
      if (stageRef.current?.app) {
        try {
          stageRef.current.app.renderer.resize(newDimensions.width, newDimensions.height);
        } catch (error) {
          console.error('❌ Error resizing PIXI stage:', error);
        }
      }
    };

    updateDimensions(); // Initial calculation
    
    // Debounce resize events for better performance
    let timeoutId;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDimensions, 100);
    };
    
    window.addEventListener('resize', debouncedUpdate);
    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      clearTimeout(timeoutId);
    };
  }, []);

  // Register stage on mount and configure for touch devices
  useEffect(() => {
    if (stageRef.current) {
      registerStage(stageRef.current);
      setIsStageRegistered(true);
      
      // Configure PIXI for touch devices
      if (stageRef.current.app) {
        const app = stageRef.current.app;
        
        // Enable touch events
        app.stage.eventMode = 'static';
        app.stage.hitArea = app.screen;
        
        // Configure for touch devices - especially tablets
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
          console.log('🖱️ Touch device detected - configuring PIXI for touch');
          
          // Disable context menu on right-click/long-press
          app.view.addEventListener('contextmenu', (e) => e.preventDefault());
          
          // Enhanced touch handling for tablets
          app.view.style.touchAction = 'none';
          
          // Add pointer event listeners for better touch handling
          app.stage.on('pointerdown', (event) => {
            console.log('👆 PIXI Touch/Pointer Down:', {
              type: event.type,
              pointerType: event.data.pointerType,
              position: { x: event.data.global.x, y: event.data.global.y },
              isPrimary: event.data.isPrimary
            });
          });
          
          app.stage.on('pointermove', (event) => {
            if (isCreatingShape && creationStartPoint) {
              console.log('👆 PIXI Touch/Pointer Move during creation');
            }
          });
          
          app.stage.on('pointerup', (event) => {
            console.log('👆 PIXI Touch/Pointer Up:', {
              type: event.type,
              pointerType: event.data.pointerType
            });
          });
        }
      }
    }
  }, [registerStage, isCreatingShape, creationStartPoint]);

  // Sync merged structures with global context
  useEffect(() => {
    console.log('🔄 Merged structures updated in Canvas:', {
      count: mergedStructures.length,
      structures: mergedStructures.map(s => ({
        id: s.id,
        shapesCount: s.shapes?.length || 0,
        createdAt: s.createdAt
      }))
    });
    
    window.dispatchEvent(new CustomEvent('mergedStructuresUpdate', {
      detail: { mergedStructures }
    }));
  }, [mergedStructures]);

  // Listen for requests for merged structures data
  useEffect(() => {
    const handleMergedStructuresRequest = () => {
      console.log('🎯 Canvas received request for merged structures:', {
        mergedStructuresCount: mergedStructures.length,
        mergedStructures: mergedStructures
      });
      
      window.dispatchEvent(new CustomEvent('provideMergedStructures', {
        detail: { mergedStructures }
      }));
      
      console.log('📤 Canvas sent merged structures response');
    };

    const handleRestoreMergedStructures = (event) => {
      const { mergedStructures: structuresToRestore } = event.detail;
      console.log('🏗️ Canvas received request to restore merged structures:', structuresToRestore);
      
      try {
        // Clear existing merged structures first
        setMergedStructures([]);
        
        // Restore merged structures with new IDs
        const restoredStructures = structuresToRestore.map((structure, index) => ({
          ...structure,
          id: nextStructureId + index,
          createdAt: Date.now()
        }));
        
        setMergedStructures(restoredStructures);
        setNextStructureId(prev => prev + structuresToRestore.length);
        
        console.log('✅ Successfully restored merged structures:', restoredStructures);
      } catch (error) {
        console.error('❌ Error restoring merged structures:', error);
      }
    };

    const handleAddShape = (event) => {
      const { shape } = event.detail;
      console.log('🎯 Adding shape to canvas:', shape);
      addShape(shape);
      // Select the shape if it's an arrow (or any shape)
      if (shape.id) {
        setTimeout(() => {
          selectShape(shape.id);
        }, 0);
      }
    };

    const handleSetCurrentTool = (event) => {
      const { tool } = event.detail;
      console.log('🎯 Setting current tool:', tool);
      setTool(tool);
    };

    window.addEventListener('requestMergedStructures', handleMergedStructuresRequest);
    window.addEventListener('restoreMergedStructures', handleRestoreMergedStructures);
    window.addEventListener('addShape', handleAddShape);
    window.addEventListener('setCurrentTool', handleSetCurrentTool);
    console.log('👂 Canvas listening for merged structures requests, restoration, addShape, and setCurrentTool events');
    
    return () => {
      window.removeEventListener('requestMergedStructures', handleMergedStructuresRequest);
      window.removeEventListener('restoreMergedStructures', handleRestoreMergedStructures);
      window.removeEventListener('addShape', handleAddShape);
      window.removeEventListener('setCurrentTool', handleSetCurrentTool);
      console.log('🔇 Canvas stopped listening for events');
    };
  }, [mergedStructures, nextStructureId, addShape, setTool]);

  // Add effect to ensure canvas is available after stage is mounted
  useEffect(() => {
    if (stageRef.current && !stageRef.current._canvas) {
      // Wait a tick for PIXI to initialize
      const timer = setTimeout(() => {
        if (stageRef.current?._canvas) {
          registerStage(stageRef.current);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [registerStage]);

  // Helper function to calculate shape dimensions from drag
  const calculateShapeDimensions = useCallback((startPoint, currentPoint, shapeType) => {
    const width = Math.abs(currentPoint.x - startPoint.x);
    const height = Math.abs(currentPoint.y - startPoint.y);
    const x = Math.min(startPoint.x, currentPoint.x);
    const y = Math.min(startPoint.y, currentPoint.y);

    // Minimum size constraints
    const minSize = 20;
    const actualWidth = Math.max(width, minSize);
    const actualHeight = Math.max(height, minSize);

    switch (shapeType) {
      case 'rectangle':
        // For rectangles, determine drawing direction based on drag
        const deltaX = currentPoint.x - startPoint.x;
        const deltaY = currentPoint.y - startPoint.y;
        
        // If dragging more horizontally, draw left to right
        // If dragging more vertically, draw top to bottom
        const isHorizontalDrag = Math.abs(deltaX) > Math.abs(deltaY);
        
        let finalWidth, finalHeight, finalX, finalY;
        
        if (isHorizontalDrag) {
          // Left to right drawing
          finalWidth = Math.abs(deltaX);
          finalHeight = Math.abs(deltaY) || minSize;
          finalX = deltaX >= 0 ? startPoint.x : currentPoint.x;
          finalY = deltaY >= 0 ? startPoint.y : currentPoint.y;
        } else {
          // Top to bottom drawing
          finalWidth = Math.abs(deltaX) || minSize;
          finalHeight = Math.abs(deltaY);
          finalX = deltaX >= 0 ? startPoint.x : currentPoint.x;
          finalY = deltaY >= 0 ? startPoint.y : currentPoint.y;
        }
        
        return {
          type: 'rectangle',
          x: finalX,
          y: finalY,
          width: Math.max(finalWidth, minSize),
          height: Math.max(finalHeight, minSize),
          rotation: 0,
          name: 'Room',
          drawDirection: isHorizontalDrag ? 'horizontal' : 'vertical'
        };
      
      case 'circle':
        const radius = Math.max(actualWidth, actualHeight) / 2;
        return {
          type: 'circle',
          x: x + radius,
          y: y + radius,
          radius: Math.max(radius, minSize / 2),
          color: 0x00cc99
        };
      
      case 'triangle':
        return {
          type: 'triangle',
          x,
          y,
          width: actualWidth,
          height: actualHeight,
          color: 0xcc9900
        };
      
      case 'door':
        const doorRadius = Math.max(actualWidth, actualHeight) / 2;
        return {
          type: 'door',
          x: x + doorRadius,
          y: y + doorRadius,
          radius: Math.max(doorRadius, 25),
          rotation: 0,
          color: 0xffd700,
          label: 'Door',
          category: 'structural',
          snappedToRoom: null
        };
      
      // For icon types, use size based on larger dimension
      default:
        if (shapeType.startsWith('icon_') || ['bed', 'chair', 'couch', 'tv', 'table', 'toilet', 'bathtub', 'stairs', 'tree', 'car'].includes(shapeType)) {
          const size = Math.max(actualWidth, actualHeight);
          const iconType = shapeType.startsWith('icon_') ? shapeType.substring(5) : shapeType;
          
          // Icon specific configurations
          const iconConfigs = {
            bed: { color: 0xff6b6b, label: 'Bed', category: 'furniture' },
            chair: { color: 0x4ecdc4, label: 'Chair', category: 'furniture' },
            couch: { color: 0x45b7d1, label: 'Sofa', category: 'furniture' },
            tv: { color: 0xf9ca24, label: 'TV', category: 'furniture' },
            table: { color: 0x6c5ce7, label: 'Dining Table', category: 'furniture' },
            toilet: { color: 0xfd79a8, label: 'Toilet', category: 'fixtures' },
            bathtub: { color: 0x00b894, label: 'Bathtub', category: 'fixtures' },
            stairs: { color: 0x74b9ff, label: 'Stairs', category: 'fixtures' },
            tree: { color: 0x00b894, label: 'Tree/Plant', category: 'outdoor' },
            car: { color: 0x636e72, label: 'Vehicle', category: 'outdoor' }
          };
          
          const config = iconConfigs[iconType] || { color: 0x666666, label: iconType, category: 'furniture' };
          
          return {
            type: 'icon',
            iconType,
            x: x + size / 2,
            y: y + size / 2,
            size: Math.max(size, 20),
            ...config
          };
        }
        
        return null;
    }
  }, []);

  // Create preview shape
  const createPreviewShape = useCallback((startPoint, currentPoint, shapeType) => {
    const dimensions = calculateShapeDimensions(startPoint, currentPoint, shapeType);
    if (!dimensions) return null;

    return {
      ...dimensions,
      id: 'preview',
      isPreview: true
    };
  }, [calculateShapeDimensions]);

  const handleCanvasMouseDown = useCallback(
    (event) => {
      if (!stageRef.current) return;

      // Get the correct coordinates for PIXI
      let globalPoint;
      if (event.data?.global) {
        // PIXI event - coordinates are already transformed by the Container
        globalPoint = { 
          x: event.data.global.x,
          y: event.data.global.y 
        };
        console.log('🖱️ PIXI Event coordinates:', globalPoint, 'zoom:', zoomLevel, 'pan:', panOffset);
      } else {
        // DOM event - convert screen coordinates to world coordinates with zoom/pan
        globalPoint = transformCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
        console.log('🖱️ DOM Event coordinates:', { 
          screen: { x: event.clientX, y: event.clientY },
          world: globalPoint,
          zoom: zoomLevel,
          pan: panOffset
        });
      }

      // Handle pan mode
      if (isPanMode) {
        setIsPanning(true);
        setPanStartPoint({
          x: event.clientX,
          y: event.clientY,
          panOffset: { ...panOffset }
        });
        return;
      }

      // Handle drawing mode
      if (isDrawingMode) {
        setIsDrawing(true);
        const newDrawingPath = {
          id: `drawing-${nextDrawingId}`,
          type: 'freehand',
          points: [{ x: globalPoint.x, y: globalPoint.y }],
          color: drawingColor.hex,
          lineWidth: drawingLineWidth,
          alpha: 0.7,
          bounds: {
            x: globalPoint.x,
            y: globalPoint.y,
            width: 0,
            height: 0
          }
        };
        setCurrentDrawingPath(newDrawingPath);
        setNextDrawingId(prev => prev + 1);
        return;
      }

      // Handle tool creation with drag-to-create
      if (currentTool && currentTool !== "select") {
        // Special validation for door tool - must have at least one rectangle or merged structure
        if (currentTool === 'door') {
          const hasRectangles = shapes.some(shape => shape.type === 'rectangle');
          const hasMergedStructures = mergedStructures && mergedStructures.length > 0;
          const hasValidContainers = hasRectangles || hasMergedStructures;
          
          console.log('🚪 DOOR VALIDATION DEBUG:', {
            totalShapes: shapes.length,
            rectangleShapes: shapes.filter(s => s.type === 'rectangle').length,
            mergedStructures: mergedStructures?.length || 0,
            hasRectangles,
            hasMergedStructures,
            hasValidContainers,
            shapeTypes: shapes.map(s => s.type)
          });
          
          if (!hasValidContainers) {
            alert('🚪 Door can only be added when there is at least one room!\n\nPlease create a rectangle first using the Rectangle tool (R), or merge existing rectangles into a room, then add doors.');
            return;
          }
        }
        
        // Check if this tool supports drag-to-create
        const dragCreateTools = ['rectangle', 'circle', 'triangle', 'door', 'bed', 'chair', 'couch', 'tv', 'table', 'toilet', 'bathtub', 'stairs', 'tree', 'car'];
        
        if (dragCreateTools.includes(currentTool)) {
          // Start drag-to-create mode
          setIsCreatingShape(true);
          setCreationStartPoint(globalPoint);
          setPreviewShape(createPreviewShape(globalPoint, globalPoint, currentTool));
          console.log('🎯 Starting shape creation at:', globalPoint, 'with tool:', currentTool);
          return;
        }
        
        // For tools that don't support drag-to-create (like line), use the old logic
        if (currentTool === "line") {
          addShape({
            type: "line",
            x: globalPoint.x,
            y: globalPoint.y,
            nodes: [
              { x: 0, y: 0 },           // Start at click point
              { x: 50, y: 0 },          // Middle node
              { x: 100, y: 0 }          // End node
            ]
          });
          setTool("select");
          return;
        }
      }

      // Handle background click (deselect)
      if (!currentTool || currentTool === "select") {
        if (!propagationBlocked.current) {
          selectShape(null);
        }
      }
    },
    [currentTool, addShape, selectShape, transformCoordinates, setTool, createPreviewShape, shapes, mergedStructures, isDrawingMode, drawingColor, drawingLineWidth, nextDrawingId, isPanMode, panOffset, zoomLevel]
  );


    // Wall alignment and angle detection helpers
    const snapToGrid = useCallback((point, gridSize = 20) => {
      return {
        x: Math.round(point.x / gridSize) * gridSize,
        y: Math.round(point.y / gridSize) * gridSize
      };
    }, []);


    const snapToAngle = useCallback((startPoint, endPoint, threshold = 10) => {
      const dx = endPoint.x - startPoint.x;
      const dy = endPoint.y - startPoint.y;
      const angle = Math.atan2(dy, dx) * 180 / Math.PI;
      
      // Snap to 0°, 45°, 90°, 135°, 180°, 225°, 270°, 315°
      const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315];
      let snappedAngle = angle;
      let minDiff = Infinity;
      
      for (const snapAngle of snapAngles) {
        const diff = Math.abs(angle - snapAngle);
        if (diff < minDiff) {
          minDiff = diff;
          snappedAngle = snapAngle;
        }
      }
      
      // Only snap if within threshold
      if (minDiff <= threshold) {
        const distance = Math.sqrt(dx * dx + dy * dy);
        const snappedRadians = snappedAngle * Math.PI / 180;
        return {
          x: startPoint.x + distance * Math.cos(snappedRadians),
          y: startPoint.y + distance * Math.sin(snappedRadians)
        };
      }
      
      return endPoint;
    }, []);

  // Handle mouse move for preview updates and drawing
  const handleCanvasMouseMove = useCallback(
    (event) => {
      if (!stageRef.current) return;

      // Get current mouse position
      let globalPoint;
      if (event.data?.global) {
        globalPoint = event.data.global;
      } else {
        globalPoint = transformCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;
      }

      // Handle panning
      if (isPanning && panStartPoint) {
        const deltaX = event.clientX - panStartPoint.x;
        const deltaY = event.clientY - panStartPoint.y;
        
        setPanOffset({
          x: panStartPoint.panOffset.x + deltaX,
          y: panStartPoint.panOffset.y + deltaY
        });
        return;
      }

      // Handle drawing mode
      if (isDrawingMode && isDrawing && currentDrawingPath) {
        const updatedPath = {
          ...currentDrawingPath,
          points: [...currentDrawingPath.points, { x: globalPoint.x, y: globalPoint.y }]
        };
        
        // Update bounds to include new point
        const allX = updatedPath.points.map(p => p.x);
        const allY = updatedPath.points.map(p => p.y);
        updatedPath.bounds = {
          x: Math.min(...allX),
          y: Math.min(...allY),
          width: Math.max(...allX) - Math.min(...allX),
          height: Math.max(...allY) - Math.min(...allY)
        };
        
        setCurrentDrawingPath(updatedPath);
        return;
      }

      // Handle shape preview updates
      if (isCreatingShape && creationStartPoint) {
        // Apply wall alignment and angle snapping for walls
        let adjustedPoint = globalPoint;
        if (currentTool === 'line' && wallAlignmentMode) {
          // Snap to grid
          adjustedPoint = snapToGrid(globalPoint);
          // Snap to angle
          adjustedPoint = snapToAngle(creationStartPoint, adjustedPoint, angleThreshold);
        }

        // Update preview shape with new dimensions
        const newPreviewShape = createPreviewShape(creationStartPoint, adjustedPoint, currentTool);
        if (newPreviewShape) {
          setPreviewShape(newPreviewShape);
        }
      }
    },
    [isCreatingShape, creationStartPoint, currentTool, transformCoordinates, createPreviewShape, wallAlignmentMode, snapToGrid, snapToAngle, angleThreshold, isDrawingMode, isDrawing, currentDrawingPath, isPanning, panStartPoint]
  );

  // Handle mouse up to finalize shape creation and drawing
  const handleCanvasMouseUp = useCallback(
    (event) => {
      if (!stageRef.current) return;

      // Handle pan end
      if (isPanning) {
        setIsPanning(false);
        setPanStartPoint(null);
        return;
      }

      // Handle drawing mode
      if (isDrawingMode && isDrawing && currentDrawingPath) {
        setIsDrawing(false);
        
        // Only save the drawing if it has enough points
        if (currentDrawingPath.points.length > 2) {
          setDrawingPaths(prev => [...prev, currentDrawingPath]);
          console.log('✅ Saved drawing path with', currentDrawingPath.points.length, 'points');
        }
        
        setCurrentDrawingPath(null);
        return;
      }

      // Handle shape creation
      if (isCreatingShape && creationStartPoint) {
        // Get final mouse position
        let globalPoint;
        if (event.data?.global) {
          globalPoint = event.data.global;
        } else {
          globalPoint = transformCoordinates(event.clientX, event.clientY);
          if (!globalPoint) return;
        }

        // Apply wall alignment and angle snapping for walls
        let adjustedPoint = globalPoint;
        if (currentTool === 'line' && wallAlignmentMode) {
          // Snap to grid
          adjustedPoint = snapToGrid(globalPoint);
          // Snap to angle
          adjustedPoint = snapToAngle(creationStartPoint, adjustedPoint, angleThreshold);
        }

        // Calculate final shape dimensions
        const finalShape = calculateShapeDimensions(creationStartPoint, adjustedPoint, currentTool);
        if (finalShape) {
          // Remove preview properties
          const { isPreview, ...shapeToAdd } = finalShape;
          addShape(shapeToAdd);
          setTool("select");
        }

        // Reset creation state
        setIsCreatingShape(false);
        setCreationStartPoint(null);
        setPreviewShape(null);
      }
    },
    [isCreatingShape, creationStartPoint, currentTool, transformCoordinates, calculateShapeDimensions, addShape, setTool, wallAlignmentMode, snapToGrid, snapToAngle, angleThreshold, isDrawingMode, isDrawing, currentDrawingPath]
  );

  useEffect(() => {
    const stopPropagation = (e) => {
      let { isBlocked } = e?.detail;
      propagationBlocked.current = isBlocked;
    };
    window.addEventListener("propogation-block", stopPropagation);
    return () => window.removeEventListener("propogation-block", stopPropagation);
  }, []);

  const renderShape = (shape) => {
    const isSelected = selectedShapeId === shape.id;

    const handleShapeUpdate = (updates) => {
      updateShape(shape.id, updates);
    };

    switch (shape.type) {
      case "rectangle":
        return (
          <Rectangle
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onUpdate={handleShapeUpdate}
            onSelect={() => selectShape(shape.id)}
            stageRef={stageRef}
            selectedMeasurementUnit={props.selectedMeasurementUnit}
            isBlueprintMode={isBlueprintMode}
          />
        );
      case "circle":
        return (
          <Circle
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onUpdate={(updates) => updateShape(shape.id, updates)}
            onSelect={() => selectShape(shape.id)}
            stageRef={stageRef}
            isBlueprintMode={isBlueprintMode}
          />
        );
      case "triangle":
        return (
          <Triangle
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onUpdate={(updates) => updateShape(shape.id, updates)}
            onSelect={() => selectShape(shape.id)}
            stageRef={stageRef}
            isBlueprintMode={isBlueprintMode}
          />
        );
      case "arrow":
        return (
          <Arrow
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onUpdate={handleShapeUpdate}
            onSelect={() => selectShape(shape.id)}
            isBlueprintMode={isBlueprintMode}
          />
        );
      case "line":
        return (
          <EditableLine
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onUpdate={handleShapeUpdate}
            onSelect={() => selectShape(shape.id)}
            stageRef={stageRef}
            allShapes={shapes}
            isBlueprintMode={isBlueprintMode}
          />
        );
      case "icon":
        return (
          <Icon
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onUpdate={handleShapeUpdate}
            onSelect={() => selectShape(shape.id)}
            stageRef={stageRef}
            isBlueprintMode={isBlueprintMode}
          />
        );
      case "door":
        return (
          <Door
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onUpdate={handleShapeUpdate}
            onSelect={() => selectShape(shape.id)}
            stageRef={stageRef}
            allShapes={shapes}
            mergedStructures={mergedStructures}
            isBlueprintMode={isBlueprintMode}
          />
        );
      case "polygonRoom":
        return (
          <PolygonRoom
            key={shape.id}
            shape={shape}
            isSelected={isSelected}
            onSelect={() => selectShape(shape.id)}
          />
        );
      default:
        return null;
    }
  };

  // Handle merge confirmation
  const handleMergeConfirm = useCallback((mergeData) => {
    const { draggedShape, targetShape, snapInfo, snapPosition } = mergeData;
    
    // Helper function to deep clone shape data to preserve EditableLine curves and other nested data
    const deepCloneShape = (shape) => {
      const cloned = JSON.parse(JSON.stringify(shape));
      
      // Log curve data preservation for debugging
      console.log('🔄 DEEP CLONE SHAPE DATA:', {
        originalId: shape.id,
        originalType: shape.type,
        originalSides: shape.sides ? shape.sides.length : 'no sides',
        originalCurveMode: shape.isCurveMode,
        originalControlPoint: shape.controlPoint,
        originalLineData: shape.lineData ? {
          length: shape.lineData.length,
          curvedSegments: shape.lineData.filter(data => data && data.isCurveMode).length,
          details: shape.lineData.map((data, i) => ({
            index: i,
            isCurveMode: data ? data.isCurveMode : false,
            hasControlPoint: data ? !!data.controlPoint : false
          }))
        } : 'no lineData',
        clonedSides: cloned.sides ? cloned.sides.length : 'no sides',
        clonedCurveMode: cloned.isCurveMode,
        clonedControlPoint: cloned.controlPoint,
        clonedLineData: cloned.lineData ? {
          length: cloned.lineData.length,
          curvedSegments: cloned.lineData.filter(data => data && data.isCurveMode).length
        } : 'no lineData cloned',
        sidesDetailPreserved: shape.sides ? shape.sides.map((side, i) => ({
          sideIndex: i,
          originalCurved: side.curved,
          originalMidControl: side.midControl,
          originalCurve: side.curve,
          clonedCurved: cloned.sides && cloned.sides[i] ? cloned.sides[i].curved : 'missing',
          clonedMidControl: cloned.sides && cloned.sides[i] ? cloned.sides[i].midControl : 'missing',
          clonedCurve: cloned.sides && cloned.sides[i] ? cloned.sides[i].curve : 'missing',
        })) : 'no curve data'
      });
      
      return cloned;
    };
    
    if (targetShape.type === 'mergedStructure') {
      // Adding to existing merged structure
      const existingStructure = mergedStructures.find(s => s.id === targetShape.id);
      if (!existingStructure) {
        console.error('Target merged structure not found:', targetShape.id);
        return;
      }

      // Deep clone the dragged shape and update position
      const clonedDraggedShape = deepCloneShape(draggedShape);
      clonedDraggedShape.x = snapPosition.x;
      clonedDraggedShape.y = snapPosition.y;

      const updatedStructure = {
        ...existingStructure,
        shapes: [
          ...existingStructure.shapes,
          clonedDraggedShape
        ]
      };

      // Update the existing structure
      setMergedStructures(prev => 
        prev.map(s => s.id === targetShape.id ? updatedStructure : s)
      );

      console.log('🔗 ADDED TO EXISTING STRUCTURE WITH PRESERVED DATA!', {
        structureId: existingStructure.id,
        addedShape: {
          id: draggedShape.id,
          type: draggedShape.type,
          newPosition: snapPosition,
          preservedData: {
            isCurveMode: clonedDraggedShape.isCurveMode,
            controlPoint: clonedDraggedShape.controlPoint,
            lineData: clonedDraggedShape.lineData ? {
              length: clonedDraggedShape.lineData.length,
              curvedSegments: clonedDraggedShape.lineData.filter(data => data && data.isCurveMode).length
            } : 'no lineData',
            hasEditableLineData: !!(clonedDraggedShape.lines || clonedDraggedShape.nodes)
          }
        },
        totalShapesInStructure: updatedStructure.shapes.length,
        snapInfo: {
          snapType: snapInfo.snapType,
          distance: snapInfo.distance
        },
        timestamp: new Date().toLocaleTimeString()
      });

      // Remove the dragged shape from individual shapes
      window.dispatchEvent(new CustomEvent('removeShapes', {
        detail: { shapeIds: [draggedShape.id] }
      }));

    } else {
      // Creating new merged structure (existing logic)
      // Deep clone both shapes to preserve all their data
      const clonedDraggedShape = deepCloneShape(draggedShape);
      clonedDraggedShape.x = snapPosition.x;
      clonedDraggedShape.y = snapPosition.y;
      
      const clonedTargetShape = deepCloneShape(targetShape);
      
      const newStructure = {
        id: `merged-${nextStructureId}`,
        type: 'mergedStructure',
        shapes: [
          clonedDraggedShape,
          clonedTargetShape
        ],
        createdAt: Date.now()
      };

      // Add to merged structures
      setMergedStructures(prev => [...prev, newStructure]);
      setNextStructureId(prev => prev + 1);

      // Console log successful merge
      console.log('🔗 NEW STRUCTURE CREATED WITH PRESERVED DATA!', {
        mergedStructureId: newStructure.id,
        draggedShape: {
          id: draggedShape.id,
          type: draggedShape.type,
          originalPosition: { x: draggedShape.x, y: draggedShape.y },
          newPosition: snapPosition,
          preservedData: {
            isCurveMode: clonedDraggedShape.isCurveMode,
            controlPoint: clonedDraggedShape.controlPoint,
            lineData: clonedDraggedShape.lineData ? {
              length: clonedDraggedShape.lineData.length,
              curvedSegments: clonedDraggedShape.lineData.filter(data => data && data.isCurveMode).length
            } : 'no lineData',
            hasEditableLineData: !!(clonedDraggedShape.lines || clonedDraggedShape.nodes)
          }
        },
        targetShape: {
          id: targetShape.id,
          type: targetShape.type,
          position: { x: targetShape.x, y: targetShape.y },
          preservedData: {
            isCurveMode: clonedTargetShape.isCurveMode,
            controlPoint: clonedTargetShape.controlPoint,
            lineData: clonedTargetShape.lineData ? {
              length: clonedTargetShape.lineData.length,
              curvedSegments: clonedTargetShape.lineData.filter(data => data && data.isCurveMode).length
            } : 'no lineData',
            hasEditableLineData: !!(clonedTargetShape.lines || clonedTargetShape.nodes)
          }
        },
        snapInfo: {
          snapType: snapInfo.snapType,
          distance: snapInfo.distance
        },
        totalShapesInStructure: newStructure.shapes.length,
        timestamp: new Date().toLocaleTimeString()
      });

      console.log('📊 MERGED STRUCTURES STATE:', {
        totalMergedStructures: mergedStructures.length + 1,
        newStructure,
        allStructures: [...mergedStructures, newStructure]
      });

      // Remove individual shapes from the shapes list
      window.dispatchEvent(new CustomEvent('removeShapes', {
        detail: { shapeIds: [draggedShape.id, targetShape.id] }
      }));
    }

  }, [nextStructureId, mergedStructures]);

  // Handle unmerge confirmation
  const handleUnmergeConfirm = useCallback((structureId) => {
    const structure = mergedStructures.find(s => s.id === structureId);
    if (!structure) return;

    // Helper function to deep clone shape data to preserve EditableLine curves and other nested data
    const deepCloneShape = (shape) => {
      return JSON.parse(JSON.stringify(shape));
    };

    // Console log successful unmerge
    console.log('🔓 UNMERGE SUCCESSFUL WITH PRESERVED DATA!', {
      unmergedStructureId: structureId,
      shapesRestored: structure.shapes.map(shape => ({
        id: shape.id,
        type: shape.type,
        position: { x: shape.x, y: shape.y },
        preservedData: {
          isCurveMode: shape.isCurveMode,
          controlPoint: shape.controlPoint,
          lineData: shape.lineData ? {
            length: shape.lineData.length,
            curvedSegments: shape.lineData.filter(data => data && data.isCurveMode).length
          } : 'no lineData',
          hasEditableLineData: !!(shape.lines || shape.nodes)
        }
      })),
      totalShapesRestored: structure.shapes.length,
      structureExistedFor: `${((Date.now() - structure.createdAt) / 1000).toFixed(1)}s`,
      timestamp: new Date().toLocaleTimeString()
    });

    // Remove from merged structures
    setMergedStructures(prev => prev.filter(s => s.id !== structureId));

    // Add individual shapes back to the main shapes list with preserved data
    structure.shapes.forEach(shape => {
      const clonedShape = deepCloneShape(shape);
      addShape(clonedShape);
    });

    // Deselect if this structure was selected
    if (selectedShapeId === structureId) {
      selectShape(null);
    }
  }, [mergedStructures, addShape, selectedShapeId, selectShape]);

  // Get all shapes (individual + merged structures for merge system)
  const getAllShapesForMergeSystem = useCallback(() => {
    const individualShapes = shapes.filter(shape => 
      !mergedStructures.some(structure => 
        structure.shapes.some(s => s.id === shape.id)
      )
    );
    
    // Add merged structures as single entities for merge calculations
    const structuresAsShapes = mergedStructures.map(structure => {
      const bounds = structure.shapes.reduce((acc, shape) => {
        const minX = Math.min(acc.minX, shape.x);
        const minY = Math.min(acc.minY, shape.y);
        const maxX = Math.max(acc.maxX, shape.x + (shape.width || 0));
        const maxY = Math.max(acc.maxY, shape.y + (shape.height || 0));
        return { minX, minY, maxX, maxY };
      }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

      return {
        id: structure.id,
        type: 'mergedStructure', // Keep original type for merge logic
        x: bounds.minX,
        y: bounds.minY,
        width: bounds.maxX - bounds.minX,
        height: bounds.maxY - bounds.minY,
        shapesCount: structure.shapes.length,
        originalStructure: structure // Keep reference to original structure
      };
    });

    return [...individualShapes, ...structuresAsShapes];
  }, [shapes, mergedStructures]);

  // Expose handlers to parent component
  useImperativeHandle(ref, () => ({
    handleMergeConfirm,
    handleUnmergeConfirm
  }), [handleMergeConfirm, handleUnmergeConfirm]);

  // Render preview shape with transparency
  const renderPreviewShape = useCallback((shape) => {
    const previewProps = {
      shape: {
        ...shape,
        alpha: 0.5, // Make it semi-transparent
        isPreview: true
      },
      isSelected: false,
      onUpdate: () => {}, // No-op for preview
      onSelect: () => {}, // No-op for preview
      stageRef
    };

    switch (shape.type) {
      case 'rectangle':
        return <Rectangle key="preview-rectangle" {...previewProps} />;
      case 'circle':
        return <Circle key="preview-circle" {...previewProps} />;
      case 'triangle':
        return <Triangle key="preview-triangle" {...previewProps} />;
      case 'door':
        return <Door key="preview-door" {...previewProps} allShapes={shapes} mergedStructures={mergedStructures} />;
      case 'icon':
        return <Icon key="preview-icon" {...previewProps} />;
      default:
        return null;
    }
  }, [stageRef, shapes, mergedStructures]);





  const detectWallAngle = useCallback((startPoint, endPoint) => {
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;
    
    // Normalize angle to 0-360
    const normalizedAngle = ((angle % 360) + 360) % 360;
    
    // Check if wall is horizontal (0° or 180°)
    const isHorizontal = Math.abs(normalizedAngle) < angleThreshold || 
                        Math.abs(normalizedAngle - 180) < angleThreshold;
    
    // Check if wall is vertical (90° or 270°)
    const isVertical = Math.abs(normalizedAngle - 90) < angleThreshold || 
                      Math.abs(normalizedAngle - 270) < angleThreshold;
    
    // Check if wall is diagonal (45°, 135°, 225°, 315°)
    const isDiagonal = Math.abs(normalizedAngle - 45) < angleThreshold ||
                      Math.abs(normalizedAngle - 135) < angleThreshold ||
                      Math.abs(normalizedAngle - 225) < angleThreshold ||
                      Math.abs(normalizedAngle - 315) < angleThreshold;
    
    return {
      angle: normalizedAngle,
      isHorizontal,
      isVertical,
      isDiagonal,
      isAligned: isHorizontal || isVertical || isDiagonal
    };
  }, [angleThreshold]);

  const getWallColor = useCallback((startPoint, endPoint) => {
    if (!wallAlignmentMode) return '#333'; // Default color
    
    const angleInfo = detectWallAngle(startPoint, endPoint);
    
    if (angleInfo.isAligned) {
      if (angleInfo.isHorizontal || angleInfo.isVertical) {
        return '#4CAF50'; // Green for perfect alignment
      } else if (angleInfo.isDiagonal) {
        return '#FF9800'; // Orange for diagonal alignment
      }
    }
    
    return '#f44336'; // Red for misaligned walls
  }, [wallAlignmentMode, detectWallAngle]);

  const [toast, setToast] = useState(null);

  useEffect(() => {
    const handleRoomDetected = (event) => {
      setToast('Room detected! You can now select, name, and edit this area like a rectangle.');
      setTimeout(() => setToast(null), 3000);
    };
    window.addEventListener('roomDetected', handleRoomDetected);
    return () => window.removeEventListener('roomDetected', handleRoomDetected);
  }, []);

  return (
    <>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1976d2',
          color: 'white',
          padding: '16px 32px',
          borderRadius: 8,
          fontSize: 18,
          fontWeight: 'bold',
          zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
        }}>
          {toast}
        </div>
      )}
    <div className={`canvas-container ${isBlueprintMode ? 'blueprint-mode' : ''}`}>
      <Stage
        ref={stageRef}
        width={canvasDimensions.width}
        height={canvasDimensions.height}
        style={{ width: "100%", height: "100%" }}
        options={{
          antialias: true,
          autoDensity: true,
          resolution: Math.max(window.devicePixelRatio || 1, 2), // Ensure minimum 2x resolution
          preserveDrawingBuffer: true,
          powerPreference: "high-performance", // Use high-performance GPU when available
          premultipliedAlpha: false,
          forceFXAA: true, // Force full scene anti-aliasing
          backgroundAlpha: 0 // Make PIXI background transparent so CSS background shows through
        }}
        onPointerDown={handleCanvasMouseDown}
        onPointerMove={handleCanvasMouseMove}
        onPointerUp={handleCanvasMouseUp}
        onTouchStart={handleCanvasMouseDown}
        onTouchMove={handleCanvasMouseMove}
        onTouchEnd={handleCanvasMouseUp}
      >
        <Container 
          scale={zoomLevel}
          position={panOffset}
        >
          {/* Render grid if enabled */}
          {showGrid && (
            <Grid
              width={canvasDimensions.width * 3} // Make grid larger to cover zoomed areas
              height={canvasDimensions.height * 3}
              gridSize={50}
              color={gridColor}
              alpha={gridAlpha}
            />
          )}
          
          {/* Render individual shapes that are not part of merged structures */}
          {isStageRegistered && shapes
            .filter(shape => {
              const isPartOfMerged = mergedStructures.some(structure => 
                structure.shapes.some(s => s.id === shape.id)
              );
              
              console.log(`🔍 SHAPE ${shape.id}:`, {
                type: shape.type,
                position: { x: shape.x, y: shape.y },
                isPartOfMerged,
                willRender: !isPartOfMerged
              });
              
              return !isPartOfMerged;
            })
            .map(renderShape)}

          {/* Render merged structures */}
          {isStageRegistered && mergedStructures.map(structure => {
            console.log(`🏗️ RENDERING MERGED STRUCTURE:`, {
              id: structure.id,
              shapesCount: structure.shapes.length,
              shapes: structure.shapes.map(s => ({ id: s.id, type: s.type, pos: { x: s.x, y: s.y } }))
            });
            
            return (
              <MergedStructure
                key={structure.id}
                structure={structure}
                isSelected={selectedShapeId === structure.id}
                onUpdate={(updatedStructure) => {
                  setMergedStructures(prev => 
                    prev.map(s => s.id === structure.id ? updatedStructure : s)
                  );
                }}
                onSelect={() => selectShape(structure.id)}
                stageRef={stageRef}
                onUnmerge={handleUnmergeConfirm}
                selectedMeasurementUnit={props.selectedMeasurementUnit}
                isBlueprintMode={isBlueprintMode}
              />
            );
          })}

          {/* Preview shape during creation */}
          {previewShape && renderPreviewShape(previewShape)}

          {/* Render closed shapes */}
          {isStageRegistered && closedShapes.map((closedShape, index) => (
            <ClosedShape
              key={closedShape.id}
              shape={closedShape}
              zIndex={-1} // Render behind other shapes
            />
          ))}

          {/* Render text annotations */}
          {annotations && annotations.map(annotation => (
            annotation.type === 'text' && (
              <AnnotationText
                key={annotation.id}
                annotation={annotation}
                setAnnotations={setAnnotations}
              />
            )
          ))}

          {/* Render saved drawing paths */}
          {drawingPaths.map(drawingPath => (
            <FreehandDraw
              key={drawingPath.id}
              shape={drawingPath}
              isSelected={selectedShapeId === drawingPath.id}
              onUpdate={() => {}} // Drawing paths are immutable once created
              onSelect={() => selectShape(drawingPath.id)}
              stageRef={stageRef}
              isBlueprintMode={isBlueprintMode}
            />
          ))}

          {/* Render current drawing path (while drawing) */}
          {currentDrawingPath && (
            <FreehandDraw
              key="current-drawing"
              shape={currentDrawingPath}
              isSelected={false}
              onUpdate={() => {}} // Current drawing is managed by state
              onSelect={() => {}} // Can't select while drawing
              stageRef={stageRef}
              isBlueprintMode={isBlueprintMode}
            />
          )}

          {/* Merge system */}
          <MergeSystem 
            shapes={getAllShapesForMergeSystem()}
            onMergeRequest={handleMergeConfirm}
          />
        </Container>
      </Stage>
      
      {/* Zoom bar controls */}
      <ZoomBar
        zoomLevel={zoomLevel}
        onResetZoom={handleResetZoom}
        isPanMode={isPanMode}
        onTogglePan={handleTogglePan}
        isZoomMode={isZoomMode}
        onToggleZoom={handleToggleZoom}
      />
    </div>
    </>
  );
});

export default Canvas;